import {useAnalytics, useClickAnalytics} from '@github-ui/use-analytics'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {ActionList, Button, Flash, type ButtonProps} from '@primer/react'
import {useEffect, useRef, useState} from 'react'

import {MergeAction, MergeQueueMethod} from '../../../types'
import {ButtonWithDropdown} from '../common/ButtonWithDropdown'
import {mergeQueueButtonText} from '../../../helpers/merge-button-text'
import {StopIcon} from '@primer/octicons-react'
import type {MergeButtonFocusProps} from './DirectMergeActionsSection'
import {MergeDropdownOption} from './MergeDropdownOption'
import type {
  MergeStateStatus,
  AutoMergeRequest,
  PullRequestMergeAction,
  PullRequestMergeRequirementsState,
} from '../../../types'
import {useEnableAutoMergeMutation} from '../../../hooks/mutations/use-enable-auto-merge-mutation'
import type {JSONAPIPullRequestPayload} from '../../../page-data/payloads/merge-box'
import type {Status} from '../../../helpers/mergeability-status'
import {MergeSectionActions} from './MergeSectionActions'
import {calculateMergeability} from '../../../helpers/mergeability-helpers'
import type {MergeBoxRollupStatus} from '../../../helpers/merge-box-status-calculator/types'
import {useAsyncMergeRequest} from '../../../hooks/mutations/use-async-merge-request'
import {MergeError} from '../../../helpers/merge-error'
import {FlashError} from '@github-ui/flash-error'
import {
  buildPullRequestUserActionContext,
  type PullRequestUserActionStack,
} from '@github-ui/pull-requests/analytics/pull-request-user-action'

type AddToMergeQueueSectionProps = {
  autoMergeRequest: AutoMergeRequest
  baseRefName: string
  handleConfirmingMergeInfo: (isConfirming: boolean) => void
  mergeCurrentlyBlockedForStacks?: boolean
  isDraft: boolean
  isInMergeQueue: boolean
  isStacked?: boolean
  mergeRequirementsState: PullRequestMergeRequirementsState
  mergeStateStatus: MergeStateStatus
  mergeBoxRollupStatus: MergeBoxRollupStatus
  onStackMergePendingChange?: (pendingAction?: PullRequestMergeAction) => void
  stackedEntriesToMergeCount?: number
  status: Status
  viewerCanAddAndRemoveFromMergeQueue: JSONAPIPullRequestPayload['viewerCanAddAndRemoveFromMergeQueue']
  viewerCanAddToMergeQueueSolo: JSONAPIPullRequestPayload['viewerCanAddToMergeQueueSolo']
  stackMetadata?: PullRequestUserActionStack
  currentStackEntryPositionNumber?: number
  stackSize?: number
  buttonSize?: ButtonProps['size']
  instructions?: React.ReactNode
}

/**
 * Renders the merge queue button and provides the ability to select from the merge queue methods
 */
export function AddToMergeQueueSection({
  autoMergeRequest,
  baseRefName,
  buttonSize,
  handleConfirmingMergeInfo,
  mergeCurrentlyBlockedForStacks = false,
  isDraft,
  isInMergeQueue,
  isStacked,
  mergeRequirementsState,
  mergeStateStatus,
  mergeBoxRollupStatus,
  onStackMergePendingChange,
  stackedEntriesToMergeCount = 1,
  status,
  viewerCanAddAndRemoveFromMergeQueue,
  viewerCanAddToMergeQueueSolo,
  stackMetadata,
  currentStackEntryPositionNumber,
  stackSize,
  shouldFocusPrimaryMergeButton,
  setShouldFocusPrimaryMergeButton,
  instructions,
}: AddToMergeQueueSectionProps & MergeButtonFocusProps) {
  const [selectedMergeQueueOption, setSelectedMergeQueueOption] = useState<MergeQueueMethod>(MergeQueueMethod.GROUP)
  const [showConfirmMergeWhenReady, setShowConfirmMergeWhenReady] = useState(false)

  // While true, a stack enqueue request is in flight.
  // Hide the action buttons so the user cannot re-trigger another enqueue.
  // State becomes false on enqueue request failure so the error can render.
  const [isPendingStackEnqueue, setIsPendingStackEnqueue] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [ruleErrors, setRuleErrors] = useState<string[]>([])
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const {sendAnalyticsEvent} = useAnalytics()

  const {mutate: enableAutoMergeMutation, isPending: isEnableAutoMergePending} = useEnableAutoMergeMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
    },
  })

  const {mutate: asyncMergeRequest, isPending: isAsyncMergeRequestPending} = useAsyncMergeRequest({
    onSuccess: () => {
      onStackMergePendingChange?.()
      handleConfirmingMergeInfo(false)
    },
    onError: (e: Error) => {
      onStackMergePendingChange?.()
      setIsPendingStackEnqueue(false)
      // Return to the initial "Enqueue stack" CTA (rather than the confirm
      // step) so the failure is shown above the primary button.
      setShowConfirmMergeWhenReady(false)
      if (e instanceof MergeError && e.ruleErrors.length > 0) {
        setRuleErrors(e.ruleErrors)
        setErrorMessage(undefined)
      } else {
        setRuleErrors([])
        setErrorMessage(e.message)
      }
    },
  })

  const isPending = isEnableAutoMergePending || isAsyncMergeRequestPending
  const shouldSendClickAnalyticsEvent = isStacked && stackMetadata && currentStackEntryPositionNumber && stackSize

  const enqueuePullRequest = () => {
    setShowConfirmMergeWhenReady(true)
    handleConfirmingMergeInfo(true)
    if (shouldSendClickAnalyticsEvent) {
      sendClickAnalyticsEvent(
        buildPullRequestUserActionContext({
          category: 'merge_box',
          action: 'stack_merge_click',
          data: {
            stack: stackMetadata,
            merge_method: selectedMergeQueueOption,
            stack_size: stackSize,
            position_in_stack: currentStackEntryPositionNumber,
            uses_merge_queue: true,
          },
        }),
      )
    }
    sendAnalyticsEvent('auto_merge_section.merge_click', 'MERGEBOX_AUTO_MERGE_BUTTON')
  }

  const createAutoMergeRequest = () => {
    if (isPending) return

    setRuleErrors([])
    setErrorMessage(undefined)

    // todo: add jump method along with permission checks for each option
    const mergeQueueMethod = selectedMergeQueueOption === MergeQueueMethod.SOLO ? 'SOLO' : 'GROUP'
    if (isStacked) {
      setIsPendingStackEnqueue(true)
      onStackMergePendingChange?.(MergeAction.MERGE_QUEUE)
      asyncMergeRequest({mergeAction: MergeAction.MERGE_QUEUE})
      sendAnalyticsEvent(
        'stacks_merge_queue_merge_section.confirm_auto_merge',
        'MERGEBOX_AUTO_MERGE_CONFIRMATION_BUTTON',
      )
    } else {
      enableAutoMergeMutation({mergeMethod: mergeQueueMethod})
    }
    handleConfirmingMergeInfo(false)
    sendAnalyticsEvent('auto_merge_section.confirm_direct_merge', 'MERGEBOX_AUTO_MERGE_CONFIRMATION_BUTTON')
  }

  const updateMergeQueueMethod = (newMergeQueueMethod: MergeQueueMethod, analyticsEvent: string) => {
    setSelectedMergeQueueOption(newMergeQueueMethod)
    sendAnalyticsEvent(analyticsEvent, 'MERGEBOX_MERGE_QUEUE_SECTION_MERGE_METHOD_MENU_ITEM')
  }

  const isMergeable = calculateMergeability(mergeRequirementsState, status, mergeStateStatus)
  const buttonIsPrimary = isMergeable && mergeBoxRollupStatus === 'ALL_PASSED'
  const mergeWhenReadyDisabled =
    !viewerCanAddAndRemoveFromMergeQueue ||
    mergeStateStatus === 'UNKNOWN' ||
    isDraft ||
    isInMergeQueue ||
    mergeCurrentlyBlockedForStacks ||
    !!autoMergeRequest

  const inactiveTooltipText = isStacked
    ? mergeRequirementsState === 'UNKNOWN'
      ? 'Checking for the ability to merge stack'
      : 'Merging is blocked due to failing stack merge requirements'
    : mergeRequirementsState === 'UNKNOWN'
      ? 'Checking for the ability to merge automatically'
      : 'Merging is blocked due to failing merge requirements'

  const defaultBranchName = baseRefName
  const mergeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (showConfirmMergeWhenReady && mergeButtonRef?.current) {
      mergeButtonRef?.current.focus()
    }
  }, [showConfirmMergeWhenReady])

  const isMergingMultiple = !!isStacked && stackedEntriesToMergeCount > 1
  const buttonCount = isMergingMultiple ? stackedEntriesToMergeCount : undefined

  return (
    <>
      {ruleErrors.length > 0 && (
        <FlashError
          prefix=""
          helpUrl=""
          errorMessageNotUsingPrefix="Repository rule violations found"
          hideRuleErrorsTitle
          ruleErrors={ruleErrors}
        />
      )}
      {errorMessage && (
        <Flash className="tmp-mb-3" variant="danger">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}
      {showConfirmMergeWhenReady ? (
        <MergeSectionActions inline>
          <MergeSectionActions.Slot>
            <Button
              size={buttonSize}
              ref={mergeButtonRef}
              count={buttonCount}
              variant="primary"
              loading={isPending || isPendingStackEnqueue}
              loadingAnnouncement={mergeQueueButtonText({
                mergeMethod: selectedMergeQueueOption,
                confirming: true,
                inProgress: true,
                isMergingMultiple,
                isStacked,
              })}
              onClick={createAutoMergeRequest}
            >
              {mergeQueueButtonText({
                mergeMethod: selectedMergeQueueOption,
                confirming: true,
                inProgress: false,
                isMergingMultiple,
                isStacked,
              })}
            </Button>
          </MergeSectionActions.Slot>
          {!isPendingStackEnqueue && (
            <MergeSectionActions.Slot>
              <Button
                size={buttonSize}
                onClick={() => {
                  handleConfirmingMergeInfo(false)
                  setShowConfirmMergeWhenReady(false)
                  sendAnalyticsEvent(
                    'auto_merge_section.cancel_auto_merge',
                    'MERGEBOX_AUTO_MERGE_CANCEL_CONFIRMATION_BUTTON',
                  )
                }}
              >
                Cancel
              </Button>
            </MergeSectionActions.Slot>
          )}
        </MergeSectionActions>
      ) : (
        <MergeSectionActions>
          <MergeSectionActions.Slot>
            <ButtonWithDropdown
              size={buttonSize}
              count={buttonCount}
              inactive={mergeWhenReadyDisabled}
              inactiveTooltipText={inactiveTooltipText}
              inactiveTooltipDirection="se"
              // We hide the secondary button and its actions if the viewer can only merge in a group
              hideSecondaryButton={!viewerCanAddToMergeQueueSolo}
              secondaryButtonAriaLabel="Select merge queue method"
              isPrimary={buttonIsPrimary}
              shouldFocusPrimaryButton={shouldFocusPrimaryMergeButton}
              onFocusPrimaryButton={() => setShouldFocusPrimaryMergeButton(false)}
              actionList={
                <ActionList selectionVariant="single">
                  <>
                    <MergeDropdownOption
                      primaryText="Queue and merge in a group"
                      secondaryText={`This pull request will be automatically grouped with other pull requests and merged into ${defaultBranchName}.`}
                      selected={selectedMergeQueueOption === MergeQueueMethod.GROUP}
                      onSelect={() =>
                        updateMergeQueueMethod(
                          MergeQueueMethod.GROUP,
                          'merqe_queue_section.select_queue_and_merge_in_a_group',
                        )
                      }
                    />
                    <MergeDropdownOption
                      primaryText="Queue and force solo merge"
                      secondaryText={`This pull request will be merged into ${defaultBranchName} by itself.`}
                      selected={selectedMergeQueueOption === MergeQueueMethod.SOLO}
                      onSelect={() =>
                        updateMergeQueueMethod(
                          MergeQueueMethod.SOLO,
                          'merqe_queue_section.select_queue_and_force_solo_merge',
                        )
                      }
                    />
                  </>
                </ActionList>
              }
              onPrimaryButtonClick={enqueuePullRequest}
            >
              {mergeQueueButtonText({
                mergeMethod: selectedMergeQueueOption,
                confirming: false,
                inProgress: isPending,
                isMergingMultiple,
                isStacked,
              })}
            </ButtonWithDropdown>
          </MergeSectionActions.Slot>
          {!!instructions && <MergeSectionActions.Slot>{instructions}</MergeSectionActions.Slot>}
        </MergeSectionActions>
      )}
    </>
  )
}
